import { useState } from 'react'
import { X, Package, CheckCircle } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    pickup_tracking: string | null
  }
  onReceived: () => void
}

export default function ReceiveReturnModal({ isOpen, onClose, returnData, onReceived }: Props) {
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const handleReceive = async () => {
    setLoading(true)
    try {
      await api.post(`/returns/admin/${returnData.id}/received`, {
        notes: notes || undefined
      })

      toast.success('Exito', `Devolucion ${returnData.code} marcada como recibida`)

      onReceived()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al marcar como recibida')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Package className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Marcar como Recibida
              </h2>
              <p className="text-sm text-slate-500">{returnData.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Info */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Confirma que has recibido el paquete de devolucion de <strong>{returnData.customer_name}</strong>.
            </p>
            {returnData.pickup_tracking && (
              <p className="text-sm text-slate-500 mt-2">
                Tracking: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{returnData.pickup_tracking}</code>
              </p>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Notas de recepcion (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Estado del paquete, observaciones..."
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-none"
            />
          </div>

          {/* Info siguiente paso */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Tras marcar como recibida, deberas realizar el testeo de los productos para verificar su estado.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleReceive}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Confirmar recepcion
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
